/**
 * BasecampCreateTodoDraftPanel
 * Shown inline on the proposing assistant message after createBasecampTodo dry-run.
 */

import { useState } from 'react';
import { Check, X, Loader2, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { testIds } from '@/lib/test-ids';
import { basecampApi } from '@/lib/basecamp-api';
import type { BasecampCreateTodoDraft } from '@/lib/basecamp-create-todo-draft';
import { looksLikeHtml } from '@/lib/basecamp-html';
import { toast } from 'sonner';

export type { BasecampCreateTodoDraft };

interface BasecampCreateTodoDraftPanelProps {
  draft: BasecampCreateTodoDraft;
  onDismiss: () => void;
  onCreated?: (todoUrl: string) => void;
}

export function BasecampCreateTodoDraftPanel({ draft, onDismiss, onCreated }: BasecampCreateTodoDraftPanelProps) {
  const [isCreating, setIsCreating] = useState(false);
  const createdUrl = draft.createdUrl ?? null;
  const created = draft.status === 'created' && Boolean(createdUrl);
  const [expanded, setExpanded] = useState(!created);

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      const result = await basecampApi.createTodo({
        todolistUrl: draft.todolistUrl,
        content: draft.content,
        description: draft.description,
        due_on: draft.due_on,
      });
      if (!result.success || result.dryRun) {
        throw new Error(result.error || 'Create did not complete');
      }
      const url = result.todoUrl || result.todo?.url;
      if (!url) {
        throw new Error('Create succeeded but no todo URL was returned');
      }
      toast.success('Basecamp todo created', {
        description: draft.content,
      });
      setExpanded(false);
      onCreated?.(url);
    } catch (error) {
      console.error('[BasecampCreateTodoDraftPanel] Create failed:', error);
      toast.error('Failed to create Basecamp todo', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const listLine = [
    draft.todolistId ? `list ${draft.todolistId}` : null,
    draft.projectId ? `bucket ${draft.projectId}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      className="w-full min-w-0 py-2"
      data-testid={testIds.chatPane.basecampCreateTodoDraft}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-muted-foreground">
            Create Basecamp todo
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              onClick={() => setExpanded((e) => !e)}
              title={expanded ? 'Collapse' : 'Expand'}
            >
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              onClick={onDismiss}
              title="Dismiss"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {expanded ? (
          <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 mb-3 text-sm space-y-2">
            {listLine ? (
              <p className="text-xs text-muted-foreground">{listLine}</p>
            ) : null}
            <p className="font-medium text-foreground">{draft.content}</p>
            {draft.description ? (
              looksLikeHtml(draft.description) ? (
                <div className="max-h-56 overflow-y-auto">
                  <div
                    className="prose prose-sm max-w-none dark:prose-invert [&_h1]:text-sm [&_h1]:font-semibold [&_h1]:text-foreground [&_h1]:mt-3 [&_h1]:mb-1 first:[&_h1]:mt-0 [&_p]:my-1 [&_p]:text-foreground [&_ul]:my-1 [&_li]:my-0 [&_pre]:text-xs [&_pre]:font-mono [&_pre]:text-foreground [&_pre]:bg-muted [&_pre]:border [&_pre]:border-border [&_pre]:p-2 [&_pre]:rounded"
                    dangerouslySetInnerHTML={{ __html: draft.description }}
                  />
                </div>
              ) : (
                <p className="text-muted-foreground whitespace-pre-wrap">{draft.description}</p>
              )
            ) : null}
            <p className="text-xs text-muted-foreground">
              Due: {draft.due_on || 'none'}
            </p>
          </div>
        ) : (
          <p className="text-sm font-medium text-foreground mb-2">{draft.content}</p>
        )}

        {created && createdUrl ? (
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
            <a
              href={createdUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline-offset-2 hover:underline break-all"
              data-testid={testIds.chatPane.basecampCreateTodoLink}
            >
              {createdUrl}
            </a>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              variant="default"
              size="sm"
              className="h-7 gap-1.5 text-xs"
              onClick={() => void handleCreate()}
              disabled={isCreating}
              data-testid={testIds.chatPane.basecampCreateTodoConfirm}
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Check className="h-3 w-3" />
                  Create todo
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={onDismiss}
              disabled={isCreating}
              data-testid={testIds.chatPane.basecampCreateTodoCancel}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
